import { useCallback, useEffect, useMemo, useState } from 'react'

import { RouteKey } from '@store/constants'

import { ProjectGroupItem } from '@api/showcase-room'

import { notifyError } from '@utilities/adgroup-utilities/notifier'
import useRoomConfigData from '@utilities/useRoomConfigData'

import { ProjectType } from '@adUtilities/constants'

import useProjectSwitchUtils from './useProjectSwitchUtils'

interface InitialValues {
  isPanelVisible: boolean
  isProcessing: boolean
}

const INITIAL_VALUES: InitialValues = {
  isPanelVisible: false,
  isProcessing: false,
}

interface UseProjectSwitchArgs {
  initialValues?: Partial<InitialValues>
  resetTriggerKey?: string | number | boolean | object
}

const useProjectSwitch = (args?: UseProjectSwitchArgs) => {
  const { initialValues: initialValuesProp, resetTriggerKey } = args || {}

  const initialValues = useMemo<InitialValues>(
    () => ({
      isPanelVisible:
        initialValuesProp?.isPanelVisible ?? INITIAL_VALUES.isPanelVisible,
      isProcessing:
        initialValuesProp?.isProcessing ?? INITIAL_VALUES.isProcessing,
    }),
    [initialValuesProp]
  )

  const [isPanelVisible, setIsPanelVisible] = useState(
    initialValues.isPanelVisible
  )
  const [isProcessing, setIsProcessing] = useState(initialValues.isProcessing)

  const { roomConfigData } = useRoomConfigData()
  const { projectSwitch, projectGroup = [] } = roomConfigData || {}

  const enable = !!projectSwitch?.enabled

  const { startProjectSwitch, validateRedirectPath } = useProjectSwitchUtils()

  const handleTogglePanel = useCallback(() => {
    setIsPanelVisible((prevVal) => !prevVal)
  }, [])

  const getRedirectPath = useCallback<(project: ProjectGroupItem) => string>(
    ({ type: projectType, route }) => {
      if (!route && projectType === ProjectType.Apartment) {
        return RouteKey.AREA_VIEW
      }
      if (!route && projectType === ProjectType.HouseAndLand) {
        return RouteKey.AREA_VIEW_HOUSE_AND_LAND
      }
      if (route && validateRedirectPath(route)) {
        return route
      }
      return RouteKey.VISION
    },
    [validateRedirectPath]
  )

  const handleProjectSwitch = useCallback(
    async (projectId: string) => {
      const projectGroupItem = projectGroup.find(
        (project) => project.id === projectId
      )
      if (!projectGroupItem) {
        return
      }

      const redirectTo = getRedirectPath(projectGroupItem)

      setIsProcessing(true)
      const { status, message } = await startProjectSwitch({
        project: {
          id: projectGroupItem.id,
          uuid: projectGroupItem.uuid,
          name: projectGroupItem.name,
          canonicalName: projectGroupItem.canonicalName,
          label: projectGroupItem.label,
        },
        redirectTo,
      })
      setIsProcessing(false)

      if (!status && message) {
        notifyError(message)
      }
    },
    [projectGroup, getRedirectPath, startProjectSwitch]
  )

  const reset = useCallback(() => {
    setIsPanelVisible(initialValues.isPanelVisible)
    setIsProcessing(initialValues.isProcessing)
  }, [initialValues])

  useEffect(() => {
    reset()
  }, [resetTriggerKey])

  return {
    enable,
    isPanelVisible,
    isProcessing,
    projectGroup,
    handleTogglePanel,
    handleProjectSwitch,
  }
}

export default useProjectSwitch
